import React, { useRef, useEffect, MutableRefObject, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Grid,
  Popper,
  Paper,
  Typography,
  IconButton,
  SvgIcon,
  Button,
  useTheme,
  alpha,
  Theme,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { RootState } from '@Common/models/redux/store';
import { detectAgent, insertTextAtCursor, getClipboardData } from 'core/utils';
import { openToastr } from '@Common/handlers/services';
import { CHAR_LIMITS } from 'config/constants';
import logger from 'core/logger/logger';
import { toggleKeyboardAction } from '@Common/actions/commonActions';
import LoadingSpinner from 'src/_common/quillElements/spinner/Loading';
import CloseIcon from '@mui/icons-material/Close';
import QuillLocalStorage from 'core/utils/localStorageClient';
import FileUploader from 'src/_common/components/fileUploader/FileUploader';
import { clipboardIcon } from 'public/images/svgPath.json';
import { getWordCount, getWordIndexes, inputWordLimitCheck } from '@Common/handlers/helper';

import { changeInputTextAction } from '../actions/quillActions';
import { charLimitMsgAction, userUploadedFile } from '../actions/controlActions';
import { textBoxesMinHeight } from '../mConstants';
import { getOutputKey } from '../handlers/logic';
import { AbsUnionGuard } from '../guards/union';
import { OutputAbs } from '../models/store';
import { contextHighlighter, keywordHighlight } from '../handlers/contextHighlight';
import { loadingKeywords, userPastedText } from '../actions/systemActions';

const useStyles = makeStyles((theme) =>
  createStyles({
    ndInputText: {
      width: '100%',
      padding: '20px 25px 20px 20px',
      'text-rendering': 'optimizeLegibility',
      '-webkit-font-smoothing': 'antialiased',
      fontSize: '16px',
      fontWeight: 400,
      outline: 'none',
      lineHeight: '1.5em',
      border: 'none',
      display: 'inline-block',
      minHeight: '50vh',
      [theme.breakpoints.down(768)]: {
        padding: '20px 13px 20px 13px',
        minHeight: 'calc(100vh - 260px)',
      },
    },
    pasteButtonStyle: {
      position: 'absolute',
      bottom: 10,
      right: 19,
      [theme.breakpoints.down('sm')]: {
        right: 14,
        bottom: 60,
      },
    },

    sumrizerMobileInput: {
      paddingRight: '30px',
      [theme.breakpoints.down('md')]: {
        paddingRight: '15px',
      },
    },

    fileDragOverlay: {
      position: 'absolute',
      bottom: 150,
      // right: '18px',
      [theme.breakpoints.down('sm')]: {
        position: 'absolute',
        bottom: 200,
        // right: 15,
        // float: 'right',
      },
    },
    onFileDropClass: {
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '20px',
      lineHeight: '27px',
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      color: theme.palette.error.main,
    },
    onDragStartClass: {
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '20px',
      lineHeight: '27px',
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      color: theme.palette.primary.main,
    },
    pasteButtonAndFileUploadStyle: {
      position: 'absolute',
      bottom: '50%',
      marginBottom: '-35px',
      // right: '18px',
      [theme.breakpoints.down('sm')]: {
        position: 'sticky',
        bottom: 100,
        // right: 15,
        // float: 'right',
      },
    },
    // hoverSVGColor: {
    //   '&:hover': {
    //     '& div': {
    //       '& svg': {
    //         '& path': {
    //           fill: theme.palette.primary.main,
    //         },
    //       },
    //     },
    //   },
    // },
    // hoverSVGColorForURL: {
    //   '&:hover': {
    //     '& div': {
    //       '& svg': {
    //         '& path:first-child': {
    //           fill: theme.palette.primary.main,
    //         },
    //       },
    //     },
    //   },
    // },
    // uploadAndPasteBox: {
    //   // boxSizing: 'border-box',
    //   // border: '1px solid transparent',
    //   '&:hover': {
    //     '& div': {
    //       '& span': {
    //         color: theme.palette.primary.main,
    //       },
    //     },
    //     // background: '#EBF2ED',
    //     // border: '1px solid rgba(76, 148, 90, 0.44)',
    //     // boxSizing: 'border-box',
    //     // borderRadius: '8px',
    //     cursor: 'pointer',
    //   },
    // },
    educationalTooltip: {
      backgroundColor: '#005DDA',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '10px',
    },
    tooltipText: {
      color: 'white',
      fontSize: '14px',
      fontWeight: 400,
    },
    iconButton: {
      color: '#FFFFFF',
    },
    // https://github.com/mui-org/material-ui/blob/4f2a07e140c954b478a6670c009c23a59ec3e2d4/docs/src/pages/components/popper/ScrollPlayground.js
    arrow: {
      overflow: 'hidden',
      position: 'absolute',
      width: '1em',
      height: '0.71em' /* = width / sqrt(2) = (length of the hypotenuse) */,
      boxSizing: 'border-box',
      color: '#005DDA',
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: '100%',
        height: '100%',
        boxShadow: theme.shadows[1],
        backgroundColor: 'currentColor',
        transform: 'rotate(45deg)',
      },
    },

    pasteButtonAndFileUploadStyleInnerBox: {
      height: '82px',
      borderRadius: '7px',
      flexWrap: 'nowrap',
    },

    popper: {
      '&[data-popper-placement*="bottom"] $arrow': {
        top: 0,
        left: '50%',
        transform: 'translateY(-50%)',
        marginTop: '-5px',
        marginLeft: 4,
        marginRight: 4,
        '&::before': {
          transformOrigin: '0 100%',
        },
      },
    },
    tooltip: {
      maxWidth: '120px',
      textAlign: 'center',
    },
    inputBoxGridHover: {
      border: `1px solid ${theme.palette.primary.dark}`,
      padding: '0px',
      borderRadius: '8px',
      cursor: 'pointer',
    },
    inputBoxGridText: {
      maxWidth: '145px',
      fontStyle: 'italic',
      fontWeight: 'normal',
      lineHeight: '16px',
      textAlign: 'center',
    },
    btnContainer: {
      fontStyle: 'italic',
      fontSize: '12px',
      lineHeight: '16px',
      textAlign: 'center',
      padding: '10px',
      width: '130px',
      height: '74px',
      cursor: 'pointer',
    },
    pasteIcon: {
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
    dropdown: {
      color: theme.palette.primary.main,
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.short,
      }),
    },
    dropdownOpen: {
      color: theme.palette.primary.main,
      transform: 'rotate(-180deg)',
    },
    dropdownClosed: {
      color: theme.palette.primary.main,
      transform: 'rotate(0)',
    },
    moreArrow: {
      position: 'relative',
      // marginTop: '12px',
      fontWeight: 'normal',
      fontSize: '12px',
      lineHeight: '16px',
      textAlign: 'center',
      display: 'flex',
    },
    fileUploaderText: {
      margin: '2px 0px -10px 0px',
      fontWeight: 600,
      fontSize: '14px',
    },
  })
);

// const Firestore = FirestoreIns.getInstance();

interface IProps {
  getText: Function;
  fileUploadArticleScrapperObject: {
    fileFormatAllowed: boolean;
    isFileDragStarted: boolean;
    showLoadingSpinner: boolean;
  };
  setFileUploadArticleScrapperObject: Function;
  setDroppedFile: Function;
  inputTextHandle: boolean;
  setInputTextHandle: Function;
  setFileInfo: Function;
  droppedFile: { files: null | File[] };
  isYtBoxOpen: boolean;
  setIsYtBoxOpen: Function;
  inputRef: MutableRefObject<HTMLInputElement>;
  extractKeyWordFromText: Function;
}

let timer: any;

function InputBox(props: IProps) {
  const LocalStorage = QuillLocalStorage.getInstance();
  const agents = {
    isIE: useSelector((state: RootState) => state.common.platform.isIE),
    isIOS: useSelector((state: RootState) => state.common.platform.isIos),
    isMobile: useSelector((state: RootState) => state.common.platform.isMobile),
    isExtension: useSelector((state: RootState) => state.common.platform.isExtension),
  };
  const user = useSelector((state: RootState) => state.user);
  const rawInputLength = useSelector((state: RootState) => state.smzr.input.numberOfChars);
  const theme = useTheme<Theme>();
  const absLengthGauge = useSelector((state: RootState) => state.smzr.controls.absLengthGauge);
  const mode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const absSummary = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, absLengthGauge);
    const isAbsType = AbsUnionGuard(state.smzr.output[outputKey]);
    return isAbsType ? (state.smzr.output[outputKey] as OutputAbs).summaryPara : '';
  });

  const extSentenceSeq = useSelector(
    (state: RootState) => state.smzr.output.ext.summarySentenceSequence
  );
  const extSentences = useSelector((state: RootState) => state.smzr.output.ext.sentences);
  const activeTab = useSelector((state: RootState) => state.smzr.controls.activeTab);
  const inputText = useSelector((state: RootState) => state.smzr.input.inputPara);
  const quilledInput = useSelector((state: RootState) => state.smzr.output.origText);
  const isQuilling = useSelector((state: RootState) => state.smzr.controls.isQuilling);
  const showCharLimitMessage = useSelector(
    (state: RootState) => state.smzr.controls.showCharLimitMessage
  );
  const forceUpdate = useSelector((state: RootState) => state.smzr.input.forceUpdateInput);
  const isKeyboardOpen = useSelector((state: RootState) => state.common.isKeyboardOpen);
  const uploadedFileInfo = useSelector((state: RootState) => state.smzr.controls.uploadedFile);
  const keywords = useSelector((state: RootState) => state.smzr.controls.keywords);

  const outputKeywords = useSelector((state: RootState) => state.smzr.output?.keywords);
  const selectedKeywords = useSelector((state: RootState) => state.smzr.controls.selectedKeywords);
  const dispatch = useDispatch();

  const isMounted = useRef(true);
  const gridContainerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);
  const [show, setShow] = useState(false);

  let currentWordLimit: number = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_GUEST;
  if (user && user.isAuthenticated) {
    currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_MEMBER;
  }
  if (user && user.premium) {
    currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_PREMIUM;
  }
  const [shouldDisplayPasteButton, setShouldDisplayPasteButton] = useState(true);

  const extensionEventHandler = (event: MessageEvent) => {
    if (event.data?.text && event.data?.method === 'setInputText') {
      logger('Extension Message Received', event.data);
      props.inputRef.current.innerText = event.data.text;
      dispatch(changeInputTextAction(event.data.text));
    }
  };

  const enableSummaryToolTip = () => {
    if (LocalStorage.get('showSummarizerInitialTooltip', 'true') === 'true') {
      let summaryTooltipNode = null;
      if (props.inputRef?.current?.children?.length) {
        summaryTooltipNode = Array.from(props.inputRef.current.children).filter(
          (childNode) => childNode.className === 'summary-educational-tooltip'
        )[0];
      }

      if (summaryTooltipNode) setAnchorEl(summaryTooltipNode);

      setTimeout(() => {
        setAnchorEl(null);
        LocalStorage.set('showSummarizerInitialTooltip', 'false');
      }, 8500);
    }
  };

  useEffect(() => {
    if (absSummary !== '' && mode === 'abs' && inputText !== '') {
      props.inputRef.current.innerHTML = contextHighlighter(inputText, absSummary, theme);
      if (selectedKeywords?.length) {
        props.inputRef.current.innerHTML = keywordHighlight(
          props.inputRef.current.innerHTML,
          selectedKeywords,
          theme
        );
      }
      enableSummaryToolTip();
    } else if (extSentenceSeq.length && mode === 'ext' && inputText !== '') {
      let extSummaryArray = '';
      extSentenceSeq.forEach((sentenceId: keyof typeof extSentences) => {
        extSummaryArray += `${extSentences[sentenceId].text} `;
      });
      props.inputRef.current.innerHTML = contextHighlighter(inputText, extSummaryArray, theme);
      enableSummaryToolTip();
    } else {
      props.inputRef.current.innerHTML = inputText;
      setAnchorEl(null);
    }
  }, [absSummary, mode, extSentenceSeq, extSentences, theme.palette.mode, outputKeywords]);

  useEffect(() => {
    if (agents.isMobile && activeTab === 'summarized') setAnchorEl(null);
    if (agents.isMobile && activeTab === 'original') enableSummaryToolTip();
  }, [agents.isMobile, activeTab]);

  useEffect(() => {
    if (!navigator || !navigator.clipboard || !navigator.clipboard.readText) {
      setShouldDisplayPasteButton(false);
    }

    // Event listeners for handling right click paste events
    document.addEventListener('contextmenu', () => {
      const rightClickPasteEvent = () => {
        dispatch(userPastedText('rightClickPaste'));
      };
      document.addEventListener('paste', rightClickPasteEvent, { once: true });
      document.addEventListener(
        'click',
        () => {
          document.removeEventListener('paste', rightClickPasteEvent);
        },
        { once: true }
      );
    });
  }, []);

  useEffect(() => {
    if (props.inputRef.current && quilledInput && quilledInput.length > 0) {
      dispatch(changeInputTextAction(quilledInput));
      if (inputText !== quilledInput && mode === 'abs') {
        dispatch(loadingKeywords(true));
        props.extractKeyWordFromText(quilledInput);
      }
    }

    if (isMounted.current) {
      isMounted.current = false;
      if (inputText && !props.inputRef.current.innerHTML) {
        props.inputRef.current.focus();
        if (agents.isIE) insertTextAtCursor(inputText);
        else document.execCommand('insertText', false, inputText);
      }
    } else if (forceUpdate) {
      props.inputRef.current.focus();
      if (agents.isIE) {
        insertTextAtCursor(inputText);
      } else document.execCommand('insertText', false, inputText);
    }
  }, [forceUpdate, isQuilling]);

  const resetFileDropStyles = () => {
    if (props.fileUploadArticleScrapperObject.isFileDragStarted) {
      props.setFileUploadArticleScrapperObject({
        ...props.fileUploadArticleScrapperObject,
        isFileDragStarted: false,
        fileFormatAllowed: true,
      });
      gridContainerRef.current.style.background = '';
      gridContainerRef.current.style.border = '';
    }
  };

  const getCaretPosition = () => {
    // console.log('---------Inside getCaret---------');
    const element = props.inputRef.current;
    let caretOffset = 0;
    if (typeof window.getSelection !== 'undefined') {
      // console.log('---------Inside Wibdow.getSelection---------');
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        if (element) {
          preCaretRange.selectNodeContents(element);
          preCaretRange.setEnd(range.endContainer, range.endOffset);
          caretOffset = preCaretRange.toString().length;
        }
        // console.log('-------- inside caret-----', preCaretRange.toString());
      } else {
        caretOffset = 0;
      }
    } else if (document.getSelection()?.type !== 'Control') {
      // console.log("---------Inside document.Selection---------");
      const textRange = document.selection.createRange();
      const preCaretTextRange = document.body.createTextRange();
      preCaretTextRange.moveToElementText(element);
      preCaretTextRange.setEndPoint('EndToEnd', textRange);
      caretOffset = preCaretTextRange.text.length;
    }
    /* console.log(
      "---------Going out of GET cursor function---------",
      caretOffset + 1
    ); */

    return caretOffset + 1;
  };

  const setCurrentCursorPosition = (char: number) => {
    /* if (document.activeElement.id === "inputText") {
      return;
    } */
    // console.log("---------Inside Set Cursor Position---------");
    function createRange(
      node: any,
      chars: { count: number },
      range?: Range | undefined
    ): Range | undefined {
      if (!range) {
        range = document.createRange();
        range.selectNode(node);
        range.setStart(node, 0);
      }

      if (chars.count === 0) {
        range.setEnd(node, chars.count);
      } else if (node !== undefined && chars.count > 0) {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.length < chars.count) {
            chars.count -= node.textContent.length;
          } else {
            range.setEnd(node, chars.count);
            chars.count = 0;
          }
        } else {
          // eslint-disable-next-line no-plusplus
          for (let lp = 0; lp < node.childNodes.length; lp++) {
            range = createRange(node.childNodes[lp], chars, range);

            if (chars.count === 0) {
              break;
            }
          }
        }
      }
      return range;
    }

    if (char >= 0) {
      // console.log("---------Inside Going to execute set cursor---------");
      const selection = window.getSelection();
      // console.log("---------Got selection---------", selection);
      if (!props.inputRef.current) return;
      const range = createRange(props.inputRef.current, {
        count: char,
      });
      if (range) {
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
        // console.log('---------Set cursor done---------', selection);
      }
    }
    // console.log("---------Going out of set cursor function---------");
  };

  const inputTextHandler = (forced = false) => {
    const currInputText = props.inputRef.current.innerText;
    if (currInputText.trim().length === 0) return;
    const cursorPos = getCaretPosition();
    const wordIndexes = getWordIndexes(currInputText);
    const currentInputwordCount = getWordCount(wordIndexes);
    if (currInputText.trim().length !== rawInputLength || forced) {
      if (currentInputwordCount > currentWordLimit) {
        if (!showCharLimitMessage) {
          dispatch(charLimitMsgAction(true));
        }
        const splitIndex = inputWordLimitCheck(wordIndexes, currentWordLimit);
        const allowedText = currInputText.substr(0, splitIndex);
        const extraText = currInputText.substr(splitIndex);

        // setting context highlight and keywords highlight in input text.
        const contextHighlight = contextHighlighter(allowedText, absSummary, theme);
        const keywordsHighlight = keywordHighlight(contextHighlight, selectedKeywords, theme);
        props.inputRef.current.innerHTML = `${keywordsHighlight}<span style="color: #818181">${extraText}</span>`;
        setCurrentCursorPosition(cursorPos - 1);
        // inputRef.current.focus();
      } else {
        props.inputRef.current.innerHTML = currInputText;
        setCurrentCursorPosition(cursorPos - 1);
        if (showCharLimitMessage) dispatch(charLimitMsgAction(false));
      }
    }

    if (currentInputwordCount <= currentWordLimit) {
      if (showCharLimitMessage) dispatch(charLimitMsgAction(false));
    }
  };

  useEffect(() => {
    inputTextHandler(true);
  }, [props.inputRef.current?.innerText, user.isAuthenticated, user.premium]);

  useEffect(() => {
    if (!props.inputRef.current.innerHTML) props.inputRef.current.innerHTML = inputText;
    if (agents.isExtension === true) {
      window.addEventListener('message', extensionEventHandler, false);
    }
    return () => {
      if (agents.isExtension === true) {
        window.removeEventListener('message', extensionEventHandler);
      }
    };
  }, []);

  const parseInput = (event: React.KeyboardEvent<HTMLDivElement>) => {
    resetFileDropStyles();
    if (uploadedFileInfo.fileName && inputText.length === 0) {
      dispatch(userUploadedFile({ fileName: '', fileType: '' }));
      inputTextHandler();
    }
    if (event && event.target && inputText !== (event.target as HTMLInputElement).innerText) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        dispatch(changeInputTextAction((event.target as HTMLInputElement).innerText));
        props.extractKeyWordFromText((event.target as HTMLInputElement).innerText);
        inputTextHandler();
      }, 500);
    }
  };

  let inputMinHeight = textBoxesMinHeight();
  if (agents.isMobile) {
    inputMinHeight = isKeyboardOpen ? 'calc(100vh - 100px)' : 'calc(100vh - 200px)';
  } else if (agents.isExtension) {
    inputMinHeight = 'inherit';
  }

  const changeInputElementStyling = () => {
    const inputEle = document.getElementById('inputBoxSummarizer');
    if (inputEle) inputEle.style.display = 'inline-block';
  };

  const pasteText = (event: React.ClipboardEvent<HTMLDivElement>) => {
    // ParaModel.uploadedFileInfo = { fileName: '', fileType: '' };
    dispatch(userUploadedFile({ fileName: '', fileType: '' }));
    props.setFileUploadArticleScrapperObject({
      ...props.fileUploadArticleScrapperObject,
      isFileDragStarted: false,
    });
    let pastedText = '';
    // console.log('in pasteText...');
    try {
      // IE
      if (window.clipboardData && window.clipboardData.getData) {
        pastedText = window.clipboardData.getData('Text');
      } else if (event.clipboardData && event.clipboardData.getData) {
        pastedText = event.clipboardData.getData('text/plain');
        // console.log('[InputBox -> pasteText]', pastedText);
      }

      // for handling multiple newline char on paste
      changeInputElementStyling();

      if (detectAgent().isIE) {
        insertTextAtCursor(pastedText);
      } else {
        document.execCommand('insertText', false, pastedText);
      }

      const inputTextLocal = props.inputRef.current.innerText;
      // if (inputTextLocal.length > currentCharLimit) {
      //   inputTextLocal = inputTextLocal.substr(0, currentCharLimit);
      //   inputTextLocal += pastedText;
      //   openToastr(`Character limit of ${currentCharLimit} exceeded, extra characters trimmed.`);
      // }
      // if (detectAgent().isIE) {
      //   inputRef.current.innerText = '';
      //   insertTextAtCursor(inputTextLocal);
      // } else {
      //   document.execCommand('selectAll', false);
      //   document.execCommand('insertText', false, inputTextLocal);
      // }
      dispatch(changeInputTextAction(inputTextLocal));
      if (mode === 'abs') {
        dispatch(loadingKeywords(true));
        props.extractKeyWordFromText(inputTextLocal);
      }
      event.preventDefault();
    } catch (err) {
      logger('ERROR PASTING', 'Error in handling the paste. Resorting to default paste behavior');
    }
    inputTextHandler();
  };

  const pasteBtnClick = () => {
    getClipboardData()
      .then((inputString: string) => {
        const text = inputString;

        props.inputRef.current.focus();
        // if (typeof text === 'string' && text.length > currentCharLimit) {
        //   text = text.substr(0, currentCharLimit);
        //   openToastr(`Character limit of ${currentCharLimit} exceeded, extra characters trimmed.`);
        // }

        // for handling multiple newline char on paste
        changeInputElementStyling();

        if (agents.isIE) {
          insertTextAtCursor(text);
        } else {
          document.execCommand('insertText', false, text);
        }
        dispatch(changeInputTextAction(text));
        if (mode === 'abs') {
          dispatch(loadingKeywords(true));
          props.extractKeyWordFromText(text);
        }
        inputTextHandler();
        dispatch(userPastedText('pasteButton', text.length));
      })
      .catch((errMsg: string) => {
        openToastr(errMsg);
        inputTextHandler();
      });
  };

  const classes = useStyles();

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (agents.isExtension || agents.isIE || props.isYtBoxOpen) {
      return;
    }
    props.setFileUploadArticleScrapperObject({
      ...props.fileUploadArticleScrapperObject,
      isFileDragStarted: true,
    });
    e.dataTransfer.dropEffect = 'copy';
    e.dataTransfer.effectAllowed = 'copy';
    gridContainerRef.current.style.background = alpha(theme.palette.primary.main, 0.15);
    gridContainerRef.current.style.border = `1px solid ${theme.palette.primary.main}`;

    // this.inputRef.current.style.cursor = "url('images/fileDragDropCursor.png'), pointer";
  };
  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (agents.isExtension || agents.isIE || props.isYtBoxOpen) {
      return;
    }
    props.setFileUploadArticleScrapperObject({
      ...props.fileUploadArticleScrapperObject,
      isFileDragStarted: false,
    });
    gridContainerRef.current.style.background = '';
    gridContainerRef.current.style.border = '';
  };
  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (agents.isExtension || agents.isIE || props.isYtBoxOpen) {
      return;
    }
    e.dataTransfer.dropEffect = 'copy';
  };

  const getDroppedFiles = (e: any) => {
    const files = [];
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i += 1) {
        if (e.dataTransfer.items[i].kind === 'file') {
          const file = e.dataTransfer.items[i].getAsFile();
          files.push(file);
        }
      }
    } else {
      for (let i = 0; i < e.dataTransfer.files.length; i += 1) {
        files.push(e.dataTransfer.files[i]);
      }
    }
    return files;
  };
  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (agents.isExtension || agents.isIE || props.isYtBoxOpen) {
      return;
    }
    // this.inputRef.current.style.background = '';
    // this.inputRef.current.style.border = '';
    props.inputRef.current.innerText = '';
    const files = getDroppedFiles(e);
    const file = files[0];
    if (file === null || file === undefined) {
      return;
    }
    const fileType = file.type;
    if (
      fileType === 'application/pdf' ||
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      resetFileDropStyles();
      props.setFileUploadArticleScrapperObject({
        ...props.fileUploadArticleScrapperObject,
        fileFormatAllowed: true,
        isFileDragStarted: false,
      });
      props.setDroppedFile({ files });
    } else {
      props.setFileUploadArticleScrapperObject({
        ...props.fileUploadArticleScrapperObject,
        fileFormatAllowed: false,
      });
      gridContainerRef.current.style.background = alpha(theme.palette.error.main, 0.15);
      gridContainerRef.current.style.border = `1px solid ${theme.palette.error.main}`;
    }
  };

  useEffect(() => {
    // if (props.inputTextHandle) {
    inputTextHandler();
    props.setInputTextHandle(false);
    // }
  }, []);

  const handleShowMore = () => {
    setShow(!show);
  };

  return (
    <Grid
      container
      id="inputGridContainerRef"
      style={{ position: 'relative' }}
      ref={gridContainerRef}
      onDrop={(e) => handleDrop(e)}
      onDragOver={(e) => handleDragOver(e)}
      onDragEnter={(e) => handleDragEnter(e)}
      onDragLeave={(e) => handleDragLeave(e)}
      onMouseDown={resetFileDropStyles}
    >
      <div className={classes.ndInputText}>
        {uploadedFileInfo.fileName && (
          <Typography
            color="textSecondary"
            style={{
              position: 'absolute',
              top: '4px',
              left: agents.isMobile ? '11px' : '18px',
              fontFamily: 'Open Sans',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '18px',
              margin: '2px',
            }}
          >
            {uploadedFileInfo.fileName}
          </Typography>
        )}
        {uploadedFileInfo?.fileName?.length > 0 && !inputText?.length ? (
          <Grid className={classes.fileUploaderText}>
            <Typography
              variant="caption"
              style={{ fontWeight: 600, fontSize: '14px' }}
              color="primary"
            >
              Please wait, Processing File...
            </Typography>
          </Grid>
        ) : null}
        <LoadingSpinner isLoading={props.fileUploadArticleScrapperObject.showLoadingSpinner} />

        <div
          role="textbox"
          tabIndex={0}
          className={classes.sumrizerMobileInput}
          contentEditable
          suppressContentEditableWarning
          placeholder="Paste or write about your topic then click the Summarize button."
          data-gramm_editor="false"
          wt-ignore-input="true"
          id="inputBoxSummarizer"
          ref={props.inputRef}
          style={{
            display: !inputText || !inputText.length ? 'block' : 'inline-block',
            minHeight: inputMinHeight,
            fontSize: agents.isExtension ? '14px' : '16px',
            // color: 'black',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            overflowY: 'auto',
            width: '100%',
            paddingTop: '4px',
            paddingBottom: mode === 'abs' && keywords.length ? '125px' : undefined, // this padding is added to keep space for keywords drawer to open in up direction
          }}
          onFocus={() => {
            resetFileDropStyles();
            if (agents.isMobile) {
              dispatch(toggleKeyboardAction(true));
              setTimeout(() => {
                window.scrollBy({
                  top: -65,
                  behavior: 'smooth',
                });
              }, 0);
            }
          }}
          onBlur={(e) => {
            resetFileDropStyles();
            if (agents.isMobile && e.target.id !== 'inputBoxSummarizer') {
              dispatch(toggleKeyboardAction(false));
              // setTimeout(() => {}, 100);
            }
          }}
          onKeyUp={parseInput}
          onPaste={pasteText}
        >
          {/* {props.inputText} */}
        </div>
        <Popper
          id="summary-educational-tooltip"
          open={Boolean(anchorEl?.getBoundingClientRect().height)}
          anchorEl={anchorEl}
          className={classes.popper}
          disablePortal
          // TODO: @breaking
          modifiers={[
            {
              name: 'offset',
              options: {
                arrow: {
                  element: arrowRef,
                },
                flip: {
                  enabled: false,
                },
              },
            },
          ]}
        >
          <Paper className={classes.educationalTooltip}>
            <span className={classes.arrow} ref={setArrowRef} />
            <Typography className={classes.tooltipText}>Text included in the summary</Typography>
            <IconButton
              className={classes.iconButton}
              onClick={() => setAnchorEl(null)}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Paper>
        </Popper>
      </div>
      {(!inputText || !inputText.length) && !agents.isExtension && !agents.isIE && (
        <>
          {props.fileUploadArticleScrapperObject.isFileDragStarted && (
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              className={classes.fileDragOverlay}
            >
              <Grid item>
                {props.fileUploadArticleScrapperObject.fileFormatAllowed ? (
                  <span className={classes.onDragStartClass}>
                    UPLOAD A PDF OR WORD (.DOCX) FILE)
                  </span>
                ) : (
                  <span className={classes.onFileDropClass}>
                    ONLY PDF and WORD (.DOCX) Files can be summarized.
                  </span>
                )}
              </Grid>
            </Grid>
          )}
          {!props.fileUploadArticleScrapperObject.isFileDragStarted && (
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              className={classes.pasteButtonAndFileUploadStyle}
            >
              <Grid item>
                <Grid
                  container
                  className={classes.pasteButtonAndFileUploadStyleInnerBox}
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  style={{
                    height: '82px',
                    borderRadius: '7px',
                    flexWrap: 'nowrap',
                    padding: '0px 3px',
                  }}
                >
                  {agents.isMobile && (
                    <Grid item style={{ paddingRight: '4px' }}>
                      <FileUploader
                        getText={props.getText}
                        droppedFile={props.droppedFile}
                        setFileInfo={props.setFileInfo}
                      />
                    </Grid>
                  )}
                  {shouldDisplayPasteButton && (
                    <Button
                      color="primary"
                      variant="outlined"
                      className={`${classes.inputBoxGridHover}`}
                      onClick={pasteBtnClick}
                    >
                      <Grid
                        container
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <div className={classes.btnContainer}>
                          <Grid>
                            <SvgIcon
                              color="primary"
                              className={classes.pasteIcon}
                              viewBox={clipboardIcon.viewBox}
                              width={clipboardIcon.width}
                              height={clipboardIcon.height}
                              style={{ fontSize: '30px' }}
                            >
                              <path {...clipboardIcon} />
                            </SvgIcon>
                          </Grid>
                          <span>Paste Text</span>
                        </div>
                      </Grid>
                    </Button>
                  )}
                </Grid>

                {/* Youtube Link Grid */}
                {/* {!show && agents.isMobile && (
                  <Grid container direction="row" justify="center" alignItems="center">
                    <Grid
                      className={classes.moreArrow}
                      style={{ marginTop: '12px' }}
                      onClick={handleShowMore}>
                      <Typography color="primary" className={classes.moreArrow}>
                        More Options
                      </Typography>

                      <div style={{ marginTop: '-4px' }}>
                        <ArrowDropDownIcon
                          className={`${classes.dropdown} ${classes.dropdownClosed}`}
                        />
                      </div>
                    </Grid>
                  </Grid>
                )} */}
                {/* {show && agents.isMobile && (
                  <div style={{ paddingTop: '5px' }}>
                    <YoutubeBtn
                      setFileUploadArticleScrapperObject={props.setFileUploadArticleScrapperObject}
                      fileUploadArticleScrapperObject={props.fileUploadArticleScrapperObject}
                      setIsYtBoxOpen={props.setIsYtBoxOpen}
                      getText={props.getText}
                      setInputTextHandle={props.setInputTextHandle}
                      inputRef={props.inputRef as any}
                    />
                  </div>
                )} */}
                {/* {show && agents.isMobile && (
                  <Grid container direction="row" justify="center" alignItems="center">
                    <Grid
                      onClick={handleShowMore}
                      className={classes.moreArrow}
                      style={{ marginTop: '12px' }}>
                      <Typography color="primary" className={classes.moreArrow}>
                        Close
                      </Typography>

                      <div style={{ marginTop: '-4px' }}>
                        <ArrowDropDownIcon
                          className={`${classes.dropdown} ${classes.dropdownOpen}`}
                        />
                      </div>
                    </Grid>
                  </Grid>
                )} */}
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
}

export default InputBox;
